import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  useLocation,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import moment from "moment";
import { cloneDeep } from "lodash";
import Page from "components/Page";
import Card from "components/Card";
import Text from "components/Text";
import Loader from "components/Loader";
import {
  deleteBusySlot,
  deleteEvent,
  getBusySlot,
  getEventListDetails,
} from "repositories/scheduler-repository";
import { decodeId, getDay } from "utils";
import constants from "../../../constants";
import SelectEmployeeModal from "../AddEvent/components/SelectEmployeeModal";
import { withTranslation } from "react-i18next";
import toast from "react-hot-toast";
import "./DeleteEvent.scss";
import {
  disabledDatesForCalendar,
  everyDatesCheckExceptCustomFunction,
} from "../Scheduler/components/Modals/repeatEvent.functions";
import { addEditEventsConst } from "../AddEvent/addEvents.constants";
import SchedulerCalendar from "../Scheduler/components/SchedulerCalendar";
import SelectOfficeModal from "../AddEvent/components/SelectOfficeModal";
import styles from "./DeleteEvent.module.scss";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import editProfileWarningIcon from "assets/images/info-icon.png";

function DeleteEvents({ t }) {
  const { effectiveOwnerId: selectedOwnerId, profile } =
    useAccountOwnerCommon();
  const history = useHistory();
  const location = useLocation();
  let { eventId } = useParams();
  let { busySlotId } = useParams();
  eventId = decodeId(eventId);
  busySlotId = decodeId(busySlotId);
  const [deleteForAll, setDeleteForAll] = useState(true);
  const [deleteOption, setDeleteOption] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSelectOfficeModalOpen, setIsSelectOfficeModalOpen] = useState(false);
  const [eventDetails, setEventDetails] = useState(null);
  const [loaderShow, setLoaderShow] = useState(true);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const [selectedDates, setSelectedDates] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [officeIdForInvitee, setOfficeIdForInvitee] = useState([]);
  const requestedDate = location?.state?.requestedDate;
  const selectedUserIdsFromStaffView =
    location?.state?.selectedUserIdsFromStaffView || [];
  const lockSelectedUsers = Boolean(location?.state?.lockSelectedUsers);
  const deleteHistory = busySlotId
    ? eventDetails?.schedulerBusySlotDeleteHistory
    : eventDetails?.schedulerEventDeleteHistory;

  const keyIsForDelete = busySlotId ? "isForAllOffices" : "isForAllStaff";
  const keyDeletedEmployees = busySlotId
    ? "schedulerBusySlotDeletedOffice"
    : "schedulerEventDeletedEmployee";
  const keyDeletedDates = busySlotId
    ? "schedulerBusySlotDeletedDate"
    : "schedulerEventDeletedDate";

  const getDetails = async () => {
    try {
      setLoaderShow(true);
      const schedularEventDate = moment(requestedDate).format("YYYY-MM-DD");
      let response;
      if (eventId) {
        response = await getEventListDetails(eventId, schedularEventDate);
      } else {
        response = await await getBusySlot({
          queryKey: [, busySlotId, schedularEventDate],
        });
      }
      if (response?.data) {
        setEventDetails(response.data);
      }
      setLoaderShow(false);
    } catch (error) {
      setLoaderShow(false);
    }
  };

  useEffect(() => {
    getDetails();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!eventId || !lockSelectedUsers || !eventDetails?.eventEmployees?.length) {
      return;
    }
    const selectedSet = new Set(selectedUserIdsFromStaffView.map((id) => Number(id)));
    const preselectedUsers = eventDetails.eventEmployees
      .filter((item) => selectedSet.has(Number(item?.user?.id)))
      .map((item) => ({ userId: item?.user?.id }));
    const fallbackSelectedUsers =
      preselectedUsers.length > 0
        ? preselectedUsers
        : selectedUserIdsFromStaffView.map((id) => ({ userId: Number(id) }));
    if (fallbackSelectedUsers.length) {
      setDeleteForAll(false);
      setUserIds(fallbackSelectedUsers);
    }
  }, [
    eventDetails?.eventEmployees,
    eventId,
    lockSelectedUsers,
    selectedUserIdsFromStaffView,
  ]);

  const goBack = useCallback(() => history.goBack(), []);

  const onChange = (date) => {
    const clickedDate = moment(date).format("YYYY-MM-DD");

    setSelectedDates((prevDates) => {
      const isDateSelected = prevDates.includes(clickedDate);

      return isDateSelected
        ? prevDates.filter((d) => d !== clickedDate)
        : [...prevDates, clickedDate];
    });
  };

  const getTileClassName = ({ date, view }) => {
    if (view !== "month") return "";

    const dateStr = moment(date).format("YYYY-MM-DD");
    const isSelected = selectedDates.includes(dateStr);

    return `${styles.calendar_tile} ${isSelected ? styles.selected : ""}`;
  };

  const startDay = useMemo(
    () => getDay(eventDetails?.date ? new Date(eventDetails.date) : new Date()),
    [eventDetails?.date]
  );

  const everyDatesCheckExceptCustom = useMemo(() => {
    return everyDatesCheckExceptCustomFunction(eventDetails);
  }, [eventDetails]);

  const disabledDates = ({ date }) => {
    return disabledDatesForCalendar(
      eventDetails,
      date,
      startDay,
      everyDatesCheckExceptCustom.get(moment(date).format("YYYY-MM")) ?? []
    );
  };
  const isDateDisabled = (date) => {
    const currentDate = moment(date).startOf("day");

    if (!eventDetails?.date || !eventDetails?.repeatedEndDate) {
      return true;
    }

    const startDate = moment(eventDetails.date).startOf("day");
    const endDate = moment(eventDetails.repeatedEndDate).endOf("day");

    const isWithinRange = currentDate.isBetween(
      startDate,
      endDate,
      "day",
      "[]"
    );
    if (!isWithinRange) return true;

    if (
      eventDetails.repeatedType ===
      addEditEventsConst.repeatedOptionsValues.everyWeek
    ) {
      const currentDayOfWeek = currentDate.day() || 7;
      const repeatedDay = eventDetails.repeatedDay;
      if (currentDayOfWeek !== repeatedDay) return true;
    }

    if (deleteHistory?.length > 0) {
      const allDeletes = deleteHistory.filter(
        (history) => history[keyIsForDelete]
      );

      for (const history of allDeletes) {
        if (
          history.deletionType === 2 &&
          history.deletedDate &&
          history.deletedDate !== "0001-01-01T00:00:00"
        ) {
          const deletedStartDate = moment(history.deletedDate).startOf("day");
          const deletedEndDate = moment(eventDetails.repeatedEndDate).endOf(
            "day"
          );
          if (
            currentDate.isBetween(deletedStartDate, deletedEndDate, "day", "[]")
          ) {
            return true;
          }
        }

        if (history[keyDeletedDates]?.length > 0) {
          const isDateDeleted = history[keyDeletedDates].some((deletedDate) =>
            moment(deletedDate.selectedDate)
              .startOf("day")
              .isSame(currentDate, "day")
          );
          if (isDateDeleted) return true;
        } else if (
          history.deletedDate &&
          history.deletedDate !== "0001-01-01T00:00:00" &&
          history.deletionType !== 2
        ) {
          if (
            moment(history.deletedDate)
              .startOf("day")
              .isSame(currentDate, "day")
          ) {
            return true;
          }
        }
      }
      const matchingIds = busySlotId ? officeIdForInvitee : userIds;
      const matchKey = busySlotId ? "officeId" : "userId";
      if (!deleteForAll && matchingIds?.length > 0) {
        const userSpecificDeletes = deleteHistory.filter((history) => {
          if (history[keyIsForDelete]) return false;
          return history[keyDeletedEmployees]?.some((deletedEmployee) =>
            userIds.some(
              (selectedUser) =>
                selectedUser?.[matchKey] === deletedEmployee?.[[matchKey]]
            )
          );
        });

        for (const history of userSpecificDeletes) {
          if (
            history.deletionType === 2 &&
            history.deletedDate &&
            history.deletedDate !== "0001-01-01T00:00:00"
          ) {
            const deletedStartDate = moment(history.deletedDate).startOf("day");
            const deletedEndDate = moment(eventDetails.repeatedEndDate).endOf(
              "day"
            );
            if (
              currentDate.isBetween(
                deletedStartDate,
                deletedEndDate,
                "day",
                "[]"
              )
            ) {
              return true;
            }
          }

          if (history[keyDeletedDates]?.length > 0) {
            const isDateDeleted = history[keyDeletedDates].some((deletedDate) =>
              moment(deletedDate.selectedDate)
                .startOf("day")
                .isSame(currentDate, "day")
            );
            if (isDateDeleted) return true;
          } else if (
            history.deletedDate &&
            history.deletedDate !== "0001-01-01T00:00:00" &&
            history.deletionType !== 2
          ) {
            if (
              moment(history.deletedDate)
                .startOf("day")
                .isSame(currentDate, "day")
            ) {
              return true;
            }
          }
        }
      }
    }

    if (moment(date).isBefore(moment().subtract(1, "day"))) {
      return true;
    }

    return disabledDates({ date });
  };

  const isDeleteFutureDisabled = () => {
    if (eventDetails?.repeatedType == 1) return true;
    if (!requestedDate || !deleteHistory) return false;

    const allStaffType2Dates = deleteHistory
      .filter(
        (history) =>
          history[keyIsForDelete] &&
          history.deletionType === 2 &&
          history.deletedDate &&
          history.deletedDate !== "0001-01-01T00:00:00"
      )
      .map((history) => moment(history.deletedDate).startOf("day"));

    const requestedMoment = moment(requestedDate).startOf("day");
    const isDisabledDueToAllStaff = allStaffType2Dates.some((deletedDate) => {
      const diffInDays = deletedDate.diff(requestedMoment, "days");
      return diffInDays <= 1;
    });

    if (isDisabledDueToAllStaff) {
      return true;
    }

    if (!deleteForAll && userIds?.length > 0) {
      const userSpecificType2Dates = deleteHistory
        .filter((history) => {
          if (history[keyIsForDelete]) return false;

          const hasMatchingUser = history[keyDeletedEmployees]?.some(
            (deletedEmployee) =>
              userIds.some(
                (selectedUser) => selectedUser.userId === deletedEmployee.userId
              )
          );

          return (
            hasMatchingUser &&
            history.deletionType === 2 &&
            history.deletedDate &&
            history.deletedDate !== "0001-01-01T00:00:00"
          );
        })
        .map((history) => moment(history.deletedDate).startOf("day"));

      return userSpecificType2Dates.some((deletedDate) => {
        const diffInDays = deletedDate.diff(requestedMoment, "days");
        return diffInDays <= 1;
      });
    }

    return false;
  };

  const isDateAlreadyDeleted = (date) => {
    if (!deleteHistory?.length) return false;

    const currentDate = moment(date).startOf("day");

    const allDeletes = deleteHistory.filter(
      (history) => history[keyIsForDelete]
    );

    for (const history of allDeletes) {
      if (
        history.deletionType === 2 &&
        history.deletedDate &&
        history.deletedDate !== "0001-01-01T00:00:00"
      ) {
        const deletedStartDate = moment(history.deletedDate).startOf("day");
        const deletedEndDate = moment(eventDetails.repeatedEndDate).endOf(
          "day"
        );

        if (
          currentDate.isBetween(deletedStartDate, deletedEndDate, "day", "[]")
        ) {
          return true;
        }
      }

      if (history[keyDeletedDates]?.length > 0) {
        const isDateDeleted = history[keyDeletedDates].some((deletedDate) =>
          moment(deletedDate.selectedDate)
            .startOf("day")
            .isSame(currentDate, "day")
        );
        if (isDateDeleted) return true;
      } else if (
        history.deletedDate &&
        history.deletedDate !== "0001-01-01T00:00:00" &&
        history.deletionType !== 2
      ) {
        if (
          moment(history.deletedDate).startOf("day").isSame(currentDate, "day")
        ) {
          return true;
        }
      }
    }

    if (!deleteForAll && userIds?.length > 0) {
      const userSpecificDeletes = deleteHistory.filter((history) => {
        if (history[keyIsForDelete]) return false;

        return history[keyDeletedEmployees]?.some((deletedEmployee) =>
          userIds.some(
            (selectedUser) => selectedUser.userId === deletedEmployee.userId
          )
        );
      });

      for (const history of userSpecificDeletes) {
        if (
          history.deletionType === 2 &&
          history.deletedDate &&
          history.deletedDate !== "0001-01-01T00:00:00"
        ) {
          const deletedStartDate = moment(history.deletedDate).startOf("day");
          const deletedEndDate = moment(eventDetails.repeatedEndDate).endOf(
            "day"
          );

          if (
            currentDate.isBetween(deletedStartDate, deletedEndDate, "day", "[]")
          ) {
            return true;
          }
        }

        if (history[keyDeletedDates]?.length > 0) {
          const isDateDeleted = history[keyDeletedDates].some((deletedDate) =>
            moment(deletedDate.selectedDate)
              .startOf("day")
              .isSame(currentDate, "day")
          );
          if (isDateDeleted) return true;
        } else if (
          history.deletedDate &&
          history.deletedDate !== "0001-01-01T00:00:00" &&
          history.deletionType !== 2
        ) {
          if (
            moment(history.deletedDate)
              .startOf("day")
              .isSame(currentDate, "day")
          ) {
            return true;
          }
        }
      }
    }

    return false;
  };

  const capitalize = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleDelete = async () => {
    if (isValidate()) {
      try {
        setLoaderShow(true);
        const data = {
          SchedulerEventId: eventDetails?.id,
          UserIds: userIds.map((val) => val.userId),
          IsForAllStaff: deleteForAll,
          DeletionType: deleteOption,
          SelectedDates: deleteOption === 3 ? selectedDates : [],
          Reason: reason,
          DeletedDate: requestedDate,
        };
        if (busySlotId) {
          data.SchedulerBusySlotId = +busySlotId;
          data.IsForAllOffices = deleteForAll;
          data.OfficeIds = officeIdForInvitee?.map((val) => val.officeId);
          delete data.SchedulerEventId;
          delete data.UserIds;
          delete data.IsForAllStaff;
        }
        let res;
        if (eventId) {
          res = await deleteEvent(data);
        } else {
          res = await deleteBusySlot(data);
        }
        toast.success(capitalize(res?.message));

        if (res) {
          setLoaderShow(false);
          history.push(constants.routes.scheduler.calendar);
        }
      } catch (err) {
        toast.error(err.message);
        setLoaderShow(false);
      }
    }
  };

  const handleSelectOffice = (value) => {
    const errorsData = cloneDeep(errors);
    setOfficeIdForInvitee(value);
    if (!value?.length) {
      errorsData["selectEmployees"] = t(
        "vendorManagement.errors.assignOfficeError"
      );
    } else {
      delete errorsData["selectEmployees"];
    }
  };

  const firstUserName = () => {
    let users = userIds;
    if (users?.length === 1 && eventDetails?.eventEmployees?.length) {
      let data = eventDetails?.eventEmployees?.find(
        (e) => e?.user?.id === users?.[0]?.userId
      );
      return `${data?.user?.firstName} ${data?.user?.lastName}`;
    } else {
      return `${users?.length} ${t("Selected")}`;
    }
  };

  const firstOfficeName = () => {
    let offices = officeIdForInvitee;
    if (
      offices?.length === 1 &&
      eventDetails?.schedulerBusySlotOffice?.length
    ) {
      let data = eventDetails?.schedulerBusySlotOffice?.find(
        (office) => office?.officeId === offices?.[0]?.officeId
      );
      return `${data?.office?.name}`;
    } else {
      return `${offices?.length} ${t("Selected")}`;
    }
  };

  const isValidate = () => {
    const errorsData = cloneDeep(errors);

    if (deleteOption === 1 && requestedDate) {
      if (isDateAlreadyDeleted(requestedDate)) {
        errorsData["event"] = t("scheduler.eventAlreadyDeleted");
        setErrors(errorsData);
        return false;
      } else {
        delete errorsData?.event;
      }
    } else {
      delete errorsData?.event;
    }

    if (deleteOption === 3 && selectedDates.length === 0) {
      errorsData["dates"] = t("scheduler.selectAlteastOneDate");
    } else {
      delete errorsData.dates;
    }
    if (eventId) {
      if (!userIds.length && !deleteForAll) {
        errorsData["selectEmployees"] = t("form.errors.emptySelection", {
          field: t("accountOwner.employees"),
        });
      } else {
        delete errorsData["selectEmployees"];
      }
    }

    if (busySlotId) {
      if (!officeIdForInvitee.length && !deleteForAll) {
        errorsData["selectEmployees"] = t(
          "vendorManagement.errors.assignOfficeError"
        );
      } else {
        delete errorsData["selectEmployees"];
      }
    }

    setErrors(errorsData);
    return !Object.values(errorsData).some((err) => !!err);
  };
  const eventKey = useMemo(
    () => ({
      eventOrShift: t(
        `accountOwner.${eventDetails?.isShift ? "shift" : "event"}Tab`
      ),
    }),
    [eventDetails?.isShift]
  );

  const eventShiftKeys = useMemo(
    () => ({
      eventOrShift: t(
        `accountOwner.${eventDetails?.isShift ? "shifts" : "events"}`
      ).toLowerCase(),
    }),
    [eventDetails?.isShift]
  );
  const createdByOthers =
    eventDetails?.isShift && profile?.id !== eventDetails?.createdById;
  return (
    <Page
      onBack={goBack}
      title={
        busySlotId
          ? t("scheduler.deleteBusyTimeslotbtn")
          : t("scheduler.deleteEvent", eventKey)
      }
    >
      {loaderShow && <Loader />}
      <Card
        className={styles.delete_event_card + " margin30"}
        radius="10px"
        marginBottom="18px"
        shadow="0 0 15px 0 rgba(0, 0, 0, 0.08)"
        >
        {createdByOthers && (
          <div className="edit-profile-warning-container mb-3">
            <div className="edit-profile-warning-bg ">
              <img
                className="edit-profile-warning-icon"
                src={editProfileWarningIcon}
                alt="warning icon"
              />
              <div className="edit-profile-warning-text">
                {t("scheduler.deletingOtherShift")}{" "}
                <strong>{eventDetails?.createdBy?.fullName}</strong>
              </div>
            </div>
          </div>
        )}
        <div
          className={`content_wrapper_new ${styles.content_wrapper}`}
          id="unique-content-wrapper"
        >
          <div className={styles.event_checkbox_container + " " + " ch-radio"}>
            <label className={`ch-radio ${lockSelectedUsers ? "disabled" : ""}`}>
              <input
                type="radio"
                name="deleteForAllOptions"
                checked={deleteForAll}
                disabled={lockSelectedUsers}
                onChange={() => setDeleteForAll(true)}
              />
              <span>
                {busySlotId
                  ? t("scheduler.deleteForAllOffices")
                  : t("scheduler.deleteForAll")}
              </span>
            </label>
            <label
              className={`ch-radio ${
                (eventId && eventDetails?.eventEmployees?.length === 1) ||
                (busySlotId &&
                  eventDetails?.schedulerBusySlotOffice?.length === 1)
                  ? "disabled"
                  : ""
              }`}
            >
              <input
                type="radio"
                name="deleteForSelectedOptions"
                checked={!deleteForAll}
                disabled={
                  lockSelectedUsers ||
                  (eventId && eventDetails?.eventEmployees?.length === 1) ||
                  (busySlotId &&
                    eventDetails?.schedulerBusySlotOffice?.length === 1)
                }
                onChange={() => {
                  const multipleEmployees =
                    eventId && eventDetails?.eventEmployees?.length > 1;
                  const multipleOffices =
                    busySlotId &&
                    eventDetails?.schedulerBusySlotOffice?.length > 1;
                  if (multipleEmployees || multipleOffices) {
                    setDeleteForAll(false);
                  }
                }}
              />
              <span>
                {busySlotId
                  ? t("scheduler.deleteForSelectedOffices")
                  : t("scheduler.deleteForSelectedEmployee")}
              </span>
            </label>
          </div>

          {!deleteForAll && !busySlotId && (
            <div className={styles.link_btn_wrapper}>
              <div className={styles.text_wrapper}>
                <Text size="13px" weight="400" color="#79869A">
                  {t("accountOwner.employees")}
                </Text>
                <Text size="14px" weight="600" color="#102C42">
                  {userIds?.length === 0
                    ? t("accountOwner.noEmployeesSelected")
                    : firstUserName()}
                </Text>
              </div>
              <span
                className="link-btn"
                style={lockSelectedUsers ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                onClick={() => !lockSelectedUsers && setIsModalOpen(true)}
              >
                {t("accountOwner.selectEmployees")}
              </span>
            </div>
          )}

          {!deleteForAll && busySlotId && (
            <div className={styles.link_btn_wrapper}>
              <div className={styles.text_wrapper}>
                <Text size="13px" weight="400" color="#79869A">
                  {t("Office(s)")}
                </Text>
                <Text size="14px" weight="600" color="#102C42">
                  {officeIdForInvitee?.length === 0
                    ? t("accountOwner.noOfficeSelected")
                    : firstOfficeName()}
                </Text>
              </div>
              <span
                className="link-btn"
                onClick={() => setIsSelectOfficeModalOpen(true)}
              >
                {t("Select")}
              </span>
            </div>
          )}

          {!deleteForAll && !userIds.length && errors.selectEmployees && (
            <span className="error-msg">{errors.selectEmployees}</span>
          )}

          <Text
            size="13px"
            marginBottom="16px"
            marginTop="25px"
            weight="400"
            color="#79869A"
          >
            {busySlotId
              ? t("scheduler.selectToDeleteBusySlot")
              : t("scheduler.selectToDeleteEvent", eventKey)}
            :
          </Text>
          <div className={`ch-radio ${styles.delete_radio_btn_wrapper}`}>
            <label>
              <input
                type="radio"
                name="deleteOptions"
                checked={deleteOption === 1}
                onChange={() => {
                  setDeleteOption(1);
                }}
              />
              <span>
                {busySlotId
                  ? t("scheduler.deleteBusySlotOnly")
                  : t("scheduler.deleteEventOnly", eventKey)}
              </span>
            </label>
            <label className={`${isDeleteFutureDisabled() && "disabled"}`}>
              <input
                type="radio"
                name="deleteOptions"
                checked={deleteOption === 2}
                disabled={isDeleteFutureDisabled()}
                onChange={() => {
                  if (!isDeleteFutureDisabled()) {
                    setDeleteOption(2);
                  }
                }}
              />
              <span>
                {busySlotId
                  ? t("scheduler.deleteAllFutureBusySlot")
                  : t("scheduler.deleteAllFutureEvents", eventShiftKeys)}
              </span>
            </label>
            <label
              className={`${eventDetails?.repeatedType == 1 && "disabled"}`}
            >
              <input
                type="radio"
                name="deleteOptions"
                checked={deleteOption === 3}
                disabled={eventDetails?.repeatedType == 1}
                onChange={() => setDeleteOption(3)}
              />
              <span>{t("scheduler.deletForSpecificeDate")}</span>
            </label>
          </div>

          {deleteOption === 3 && (
            <>
              <SchedulerCalendar
                onChange={onChange}
                className={`scheduler-calendar ${styles.custom_calendar}`}
                tileDisabled={({ date }) => isDateDisabled(date)}
                minDate={
                  eventDetails?.date ? new Date(eventDetails.date) : null
                }
                maxDate={
                  eventDetails?.repeatedEndDate
                    ? new Date(eventDetails.repeatedEndDate)
                    : null
                }
                tileClassName={getTileClassName}
                value={null}
              />
              {errors?.dates && (
                <span className="error-msg mt-2">{errors.dates}</span>
              )}
            </>
          )}

          <div className="c-field mt-4">
            <label>
              {busySlotId
                ? t("scheduler.reasonForDeleteBusyTimeSlot")
                : t("scheduler.reasonForDelete", eventKey)}
            </label>
            <textarea
              className="c-form-control"
              placeholder="Write a reason"
              name="Reason for delete event"
              maxLength="500"
              onChange={(e) => setReason(e.target.value)}
              value={reason}
            />
            {errors?.reason && (
              <span className="error-msg mt-2">{errors.reason}</span>
            )}
          </div>
          {errors?.event && (
            <span className="error-msg mt-2 mb-2">{errors.event}</span>
          )}
          <button
            className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
            onClick={handleDelete}
          >
            {busySlotId
              ? t("scheduler.deleteBusyTimeslotbtn")
              : t("scheduler.deleteEvent", eventKey)}
          </button>
          <button
            className="button button-round button-border button-dark btn-mobile-link"
            onClick={goBack}
          >
            {t("notes.cancel")}
          </button>
        </div>
      </Card>

      {isModalOpen && (
        <SelectEmployeeModal
          t={t}
          issaveEmployeeModalOpen={isModalOpen}
          setIsSaveEmployeeModalOpen={setIsModalOpen}
          allMembersList={
            eventDetails?.eventEmployees
              ?.filter((item) => !item?.isDeletedForThisSchedulerEvent)
              ?.map((val) => ({
                ...val,
                ...val?.user,
              })) || []
          }
          memberIds={userIds}
          setMemberIds={setUserIds}
          officeType={eventDetails?.officeId}
          enableOfficeFilter
        />
      )}
      {!!isSelectOfficeModalOpen && (
        <SelectOfficeModal
          isOpen={!!isSelectOfficeModalOpen}
          closeStepOneModal={setIsSelectOfficeModalOpen}
          ownerId={selectedOwnerId}
          fromSelectOffice={true}
          setMemberIds={(value) => {
            handleSelectOffice(value);
          }}
          memberIds={officeIdForInvitee}
          isFromBusySlotDeleteList={true}
          busySlotOfficeListIds={
            eventDetails?.schedulerBusySlotOffice
              ?.filter((item) => !item?.isDeletedForThisSchedulerBusySlot)
              ?.filter((office) => office?.officeId)
              ?.map((office) => office?.officeId) || []
          }
        />
      )}
      {/* this code needed for future integration */}
      {/* {isModalOpen && (
              <SelectStaffMemberModal
                isOpen={isModalOpen}
                closeStepOneModal={setIsModalOpen}
                memberIds={userIds}
                setMemberIds={setUserIds}
                allMembersList={
                  eventDetails?.eventEmployees
                    ?.filter((item) => !item?.isDeletedForThisSchedulerEvent)
                    ?.map((val) => ({
                      ...val,
                      ...val?.user,
                    })) || []
                }
              />
            )} */}
    </Page>
  );
}

export default withTranslation()(DeleteEvents);
